import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCruiseMetadata } from '../context/CruiseMetadataContext';
import { 
  ChevronLeft, 
  Anchor, 
  Calendar, 
  Wrench, 
  Ship,
  TrendingUp,
  BarChart3,
  ExternalLink
} from 'lucide-react';
import { motion } from 'framer-motion';
import LazyImage from '../components/LazyImage';
import CustomCruiseIcon from '../components/CustomCruiseIcon';
import { GenericPageSkeleton } from '../components/LoadingSkeleton';
import { useDisplayPreferences } from '../hooks/useDisplayPreferences';

interface ShipInClass {
  cruiseLineName: string;
  shipName: string;
  yearBuilt?: string;
  lastRefurbishment?: string;
  grossTonnage?: string;
  length?: string;
  width?: string;
}

const ShipClass = () => {
  const { shipClass } = useParams<{ shipClass: string }>();
  const navigate = useNavigate();
  const { cruiseLines, loading: metadataLoading } = useCruiseMetadata();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);

  // Decode URL parameter and trim whitespace for consistent matching
  const decodedShipClass = shipClass ? decodeURIComponent(shipClass).trim() : '';

  // Ensure skeleton shows for minimum duration
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Track when data is actually loaded
  useEffect(() => {
    if (!metadataLoading) {
      setDataLoaded(true);
    } else {
      setDataLoaded(false);
    }
  }, [metadataLoading]);

  // Find all ships in the same class
  const shipsInClass = useMemo(() => {
    if (!decodedShipClass || !cruiseLines || cruiseLines.length === 0) {
      return [];
    }

    const ships: ShipInClass[] = [];

    for (const cruiseLine of cruiseLines) {
      if (!cruiseLine.shipMetadata) continue;

      for (const [shipName, metadata] of Object.entries(cruiseLine.shipMetadata)) {
        const classKey = metadata.normalizedClass?.trim() || metadata.shipClass?.trim() || '';
        // Case-insensitive comparison
        if (classKey && classKey.toLowerCase() === decodedShipClass.toLowerCase()) {
          ships.push({
            cruiseLineName: cruiseLine.name,
            shipName,
            yearBuilt: metadata.yearBuilt,
            lastRefurbishment: metadata.lastRefurbishment,
            grossTonnage: metadata.grossTonnage,
            length: metadata.length,
            width: metadata.width
          });
        }
      }
    }

    return ships;
  }, [decodedShipClass, cruiseLines]);

  // Calculate maximum number of data points across all ships
  const maxDataPoints = useMemo(() => {
    if (shipsInClass.length === 0) return 0;
    
    return Math.max(...shipsInClass.map(ship => {
      let count = 0;
      if (ship.yearBuilt) count++;
      if (ship.lastRefurbishment) count++;
      if (ship.grossTonnage) count++;
      if (ship.length || ship.width) count++;
      return count;
    }));
  }, [shipsInClass]);

  // Group ships by cruise line
  const shipsByCruiseLine = useMemo(() => {
    const groups: { [key: string]: ShipInClass[] } = {};

    shipsInClass.forEach(ship => {
      if (!groups[ship.cruiseLineName]) {
        groups[ship.cruiseLineName] = [];
      }
      groups[ship.cruiseLineName].push(ship);
    });

    // Convert to array and sort
    return Object.entries(groups)
      .map(([cruiseLineName, ships]) => ({
        cruiseLineName,
        ships: ships.sort((a, b) => a.shipName.localeCompare(b.shipName))
      }))
      .sort((a, b) => a.cruiseLineName.localeCompare(b.cruiseLineName));
  }, [shipsInClass]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (shipsInClass.length === 0) {
      return null;
    }

    // Oldest and newest ships by yearBuilt
    const shipsWithYearBuilt = shipsInClass.filter(s => s.yearBuilt);
    let oldestShip: ShipInClass | null = null;
    let newestShip: ShipInClass | null = null;

    if (shipsWithYearBuilt.length > 0) {
      oldestShip = shipsWithYearBuilt.reduce((oldest, current) => {
        const oldestYear = parseInt(oldest.yearBuilt || '9999', 10);
        const currentYear = parseInt(current.yearBuilt || '9999', 10);
        return currentYear < oldestYear ? current : oldest;
      });

      newestShip = shipsWithYearBuilt.reduce((newest, current) => {
        const newestYear = parseInt(newest.yearBuilt || '0', 10);
        const currentYear = parseInt(current.yearBuilt || '0', 10);
        return currentYear > newestYear ? current : newest;
      });
    }

    // Most recently refurbished
    const shipsWithRefurbishment = shipsInClass.filter(s => s.lastRefurbishment);
    let mostRecentlyRefurbished: ShipInClass | null = null;

    if (shipsWithRefurbishment.length > 0) {
      mostRecentlyRefurbished = shipsWithRefurbishment.reduce((latest, current) => {
        const latestYear = parseInt(latest.lastRefurbishment || '0', 10);
        const currentYear = parseInt(current.lastRefurbishment || '0', 10);
        return currentYear > latestYear ? current : latest;
      });
    }

    // Breakdown by cruise line
    const cruiseLineBreakdown: { [key: string]: number } = {};
    shipsInClass.forEach(ship => {
      cruiseLineBreakdown[ship.cruiseLineName] = (cruiseLineBreakdown[ship.cruiseLineName] || 0) + 1;
    });

    return {
      oldestShip,
      newestShip,
      mostRecentlyRefurbished,
      cruiseLineBreakdown,
      totalShips: shipsInClass.length
    };
  }, [shipsInClass]);

  const handleBackNavigation = () => {
    navigate(-1);
  };

  // Show skeleton until both data is loaded AND minimum time has elapsed
  if (!dataLoaded || !minTimeElapsed) {
    return <GenericPageSkeleton />;
  }

  // Handle missing shipClass parameter
  if (!decodedShipClass) {
    return (
      <div className="space-y-6">
        <div className="flex items-center">
          <button 
            onClick={handleBackNavigation}
            className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-accent"
            aria-label="Go back"
          >
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Ship Class Not Found</h1>
            <p className="text-gray-600">No ship class specified.</p>
          </div>
        </div>
      </div>
    );
  }

  // Handle shipClass with no matching ships
  if (shipsInClass.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <div className="mb-6 flex items-center">
          <button 
            onClick={handleBackNavigation}
            className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-accent"
            aria-label="Go back"
          >
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">{decodedShipClass}</h1>
            <p className="text-gray-600">Ship Class</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <Ship className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Ships Found</h3>
          <p className="text-gray-500">
            No ships found in the {decodedShipClass} class.
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: dataLoaded ? 1 : 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <div className="mb-6 flex items-center">
        <button 
          onClick={handleBackNavigation}
          className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-accent"
          aria-label="Go back"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-800">{decodedShipClass}</h1>
          <p className="text-gray-600">Ship Class</p>
        </div>
      </div>

      {/* Summary Statistics Cards */}
      {summaryStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Oldest Ship */}
          {summaryStats.oldestShip && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <div className="flex items-center mb-3">
                <Calendar className="h-5 w-5 text-teal-500 mr-2" />
                <h3 className="font-semibold text-gray-900">Oldest Ship</h3>
              </div>
              <p className="text-2xl font-bold text-gray-800 mb-1">
                {summaryStats.oldestShip.shipName}
              </p>
              <p className="text-sm text-gray-600">
                {summaryStats.oldestShip.cruiseLineName} • Built {summaryStats.oldestShip.yearBuilt}
              </p>
            </motion.div>
          )}

          {/* Newest Ship */}
          {summaryStats.newestShip && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <div className="flex items-center mb-3">
                <TrendingUp className="h-5 w-5 text-teal-500 mr-2" />
                <h3 className="font-semibold text-gray-900">Newest Ship</h3>
              </div>
              <p className="text-2xl font-bold text-gray-800 mb-1">
                {summaryStats.newestShip.shipName}
              </p>
              <p className="text-sm text-gray-600">
                {summaryStats.newestShip.cruiseLineName} • Built {summaryStats.newestShip.yearBuilt}
              </p>
            </motion.div>
          )}

          {/* Most Recently Refurbished */}
          {summaryStats.mostRecentlyRefurbished && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <div className="flex items-center mb-3">
                <Wrench className="h-5 w-5 text-teal-500 mr-2" />
                <h3 className="font-semibold text-gray-900">Most Recently Refurbished</h3>
              </div>
              <p className="text-2xl font-bold text-gray-800 mb-1">
                {summaryStats.mostRecentlyRefurbished.shipName}
              </p>
              <p className="text-sm text-gray-600">
                {summaryStats.mostRecentlyRefurbished.cruiseLineName} • Refurbished {summaryStats.mostRecentlyRefurbished.lastRefurbishment}
              </p>
            </motion.div>
          )}

          {/* Total Ships */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-md p-6"
          >
            <div className="flex items-center mb-3">
              <BarChart3 className="h-5 w-5 text-teal-500 mr-2" />
              <h3 className="font-semibold text-gray-900">Total Ships</h3>
            </div>
            <p className="text-2xl font-bold text-gray-800 mb-1">
              {summaryStats.totalShips}
            </p>
            <p className="text-sm text-gray-600">
              Across {Object.keys(summaryStats.cruiseLineBreakdown).length} cruise line{Object.keys(summaryStats.cruiseLineBreakdown).length !== 1 ? 's' : ''}
            </p>
          </motion.div>
        </div>
      )}

      {/* Cruise Line Breakdown */}
      {summaryStats && Object.keys(summaryStats.cruiseLineBreakdown).length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-md p-6 mb-8"
        >
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <BarChart3 className="h-5 w-5 text-teal-500 mr-2" />
            Breakdown by Cruise Line
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Object.entries(summaryStats.cruiseLineBreakdown)
              .sort((a, b) => b[1] - a[1])
              .map(([cruiseLineName, count]) => (
                <div key={cruiseLineName} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium text-gray-900">{cruiseLineName}</span>
                  <span className="text-lg font-bold text-teal-600">{count}</span>
                </div>
              ))}
          </div>
        </motion.div>
      )}

      {/* Ships Grouped by Cruise Line */}
      <div className="space-y-8">
        {shipsByCruiseLine.map((group, index) => (
          <motion.div
            key={group.cruiseLineName}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow-md overflow-hidden"
          >
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-800 flex items-center">
                <Anchor className="h-6 w-6 text-teal-600 mr-2" />
                {group.cruiseLineName}
                <span className="ml-3 text-sm font-normal text-gray-500">
                  ({group.ships.length} ship{group.ships.length !== 1 ? 's' : ''})
                </span>
              </h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {group.ships.map((ship) => (
                  <ShipCard
                    key={`${ship.cruiseLineName}_${ship.shipName}`}
                    ship={ship}
                    maxDataPoints={maxDataPoints}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

// Ship Card Component
const ShipCard = ({ ship, maxDataPoints }: { ship: ShipInClass; maxDataPoints: number }) => {
  const { getShipImageUrl } = useCruiseMetadata();
  const { formatTonnage, formatDimensions } = useDisplayPreferences();
  const [imageError, setImageError] = useState(false);
  const shipImageUrl = getShipImageUrl(ship.cruiseLineName, ship.shipName);
  const shipDetailsUrl = `/ship/${encodeURIComponent(ship.cruiseLineName)}/${encodeURIComponent(ship.shipName)}`;

  useEffect(() => {
    setImageError(false);
  }, [shipImageUrl]);

  // Calculate min-height based on max data points
  // Each data point row: text-xs (12px) + space-y-2 gap (8px) = ~20px per row
  // First row: 12px, subsequent rows: 20px each
  // Formula: 12px (first) + (maxDataPoints - 1) * 20px
  const minHeight = maxDataPoints > 0 
    ? `${12 + (maxDataPoints - 1) * 20}px`
    : 'auto';

  return (
    <Link to={shipDetailsUrl} className="h-full">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ scale: 1.02 }}
        className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer transition-shadow hover:shadow-lg border border-gray-200 flex flex-col h-full"
      >
        <div className="h-40 relative overflow-hidden bg-gradient-to-r from-blue-900 to-blue-700 flex-shrink-0">
          {shipImageUrl && !imageError ? (
            <>
              <LazyImage 
                src={shipImageUrl}
                alt={ship.shipName}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
              <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-black/40 to-transparent"></div>
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <CustomCruiseIcon size={64} className="text-white/30" />
            </div>
          )}
        </div>
        <div className="p-4 flex flex-col flex-1">
          <h4 className="font-semibold text-sm text-gray-900 mb-3">
            {ship.shipName}
          </h4>
          <div 
            className="space-y-2 text-xs text-gray-600 mb-3 flex-1"
            style={{ minHeight }}
          >
            {ship.yearBuilt && (
              <div className="flex items-center">
                <Calendar className="h-3 w-3 mr-2 text-gray-400" />
                <span>Built: {ship.yearBuilt}</span>
              </div>
            )}
            {ship.lastRefurbishment && (
              <div className="flex items-center">
                <Wrench className="h-3 w-3 mr-2 text-gray-400" />
                <span>Refurbished: {ship.lastRefurbishment}</span>
              </div>
            )}
            {ship.grossTonnage && (
              <div className="flex items-center">
                <Ship className="h-3 w-3 mr-2 text-gray-400" />
                <span>Tonnage: {formatTonnage(ship.grossTonnage)}</span>
              </div>
            )}
            {(ship.length || ship.width) && (
              <div className="flex items-center">
                <Anchor className="h-3 w-3 mr-2 text-gray-400" />
                <span>{formatDimensions(ship.length, ship.width)}</span>
              </div>
            )}
          </div>
          <div className="flex items-center justify-end pt-3 border-t border-gray-200 mt-auto">
            <div className="flex items-center text-sm font-medium text-secondary-600 hover:text-secondary-500 transition-colors duration-200">
              <ExternalLink className="h-4 w-4 mr-1" />
              View Details
            </div>
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

export default ShipClass;

